import { useQuery } from '@tanstack/react-query';
import {
  DocumentTextIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ClockIcon,
  BanknotesIcon,
  BriefcaseIcon,
  CurrencyRupeeIcon,
  CubeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Card, Badge, LoadingSpinner } from '@/components/common';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import { formatCurrency } from '@/utils';
import { INSURER_CARD_TARGETS } from '@/pages/dashboard/cardTargets';
import { PAID_HELP, PAYABLE_HELP } from './CommissionPage';

// #643: the API returns null (not 0) for a block of figures the caller's role
// does not cover (products / channels / commission); those cards and panels
// are not rendered.
interface DashboardData {
  total_policies: number | null;
  active_policies: number | null;
  total_broker_relations: number | null;
  active_broker_relations: number | null;
  pending_broker_relations: number | null;
  approved_policy_access: number | null;
  // #350: issued business on this insurer's paper (see the backend's
  // `_issued_business`). Optional so an older API answer still renders.
  issued_policies?: number;
  active_issued_policies?: number;
  gross_written_premium?: string;
  total_claims?: number;
  total_commission?: string | null;
  // #410: paid out so far, and earned minus paid (backend `commission_summary`).
  // #643: null when the caller's role does not cover commission figures.
  commission_paid?: string | null;
  commission_payable?: string | null;
  recent_policies: Array<{
    id: string;
    name: string;
    code: string;
    is_active: boolean;
  }> | null;
  broker_relations: Array<{
    id: string;
    broker_name: string;
    status: string;
  }> | null;
}

function formatAmount(value: string | null | undefined): string {
  return formatCurrency(value ?? '0');
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  PENDING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  DISABLED_BY_INSURER: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function InsurerDashboardPage() {
  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['insurerDashboard'],
    queryFn: async () => {
      const response = await api.get('/insurers/insurance/broker-management/dashboard/');
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // #350: the numbers an insurer asked for -- its issued business first, then
  // its broker network. "Products Offered" is what this page used to call
  // "Total Policies" (it counts InsurancePolicy products, not issued policies).
  const stats = [
    { label: 'Total Policies', value: data?.issued_policies ?? 0, icon: DocumentTextIcon, color: 'text-blue-600 dark:text-blue-400' },
    { label: 'Active Policies', value: data?.active_issued_policies ?? 0, icon: ShieldCheckIcon, color: 'text-green-600 dark:text-green-400' },
    { label: 'Total Premium (GWP)', value: formatAmount(data?.gross_written_premium), icon: CurrencyRupeeIcon, color: 'text-indigo-600 dark:text-indigo-400' },
    { label: 'Claims', value: data?.total_claims ?? 0, icon: BriefcaseIcon, color: 'text-red-600 dark:text-red-400' },
    // #410: Paid = payouts already made; Payable = earned commission not yet
    // paid out (earned minus paid). Both come from the one backend definition
    // the Commission page uses, and both open that page.
    {
      label: 'Commission Paid',
      withheld: data?.commission_paid === null,
      value: formatAmount(data?.commission_paid),
      icon: BanknotesIcon,
      color: 'text-emerald-600 dark:text-emerald-400',
      tooltip: PAID_HELP,
    },
    {
      label: 'Commission Payable',
      withheld: data?.commission_payable === null,
      value: formatAmount(data?.commission_payable),
      icon: BanknotesIcon,
      color: 'text-emerald-600 dark:text-emerald-400',
      tooltip: PAYABLE_HELP,
    },
    { label: 'Active Brokers', withheld: data?.active_broker_relations === null, value: data?.active_broker_relations ?? 0, icon: UserGroupIcon, color: 'text-purple-600 dark:text-purple-400' },
    { label: 'Pending Broker Requests', withheld: data?.pending_broker_relations === null, value: data?.pending_broker_relations ?? 0, icon: ClockIcon, color: 'text-yellow-600 dark:text-yellow-400' },
    { label: 'Products Offered', withheld: data?.total_policies === null, value: data?.total_policies ?? 0, icon: CubeIcon, color: 'text-cyan-600 dark:text-cyan-400' },
    // Owner decision (security review of #350): kept beside the business
    // metrics, labelled so it is not read as volume -- it counts product x
    // broker access pairs that are switched on.
    {
      label: 'Policy Access Granted',
      withheld: data?.approved_policy_access === null,
      value: data?.approved_policy_access ?? 0,
      icon: CheckCircleIcon,
      color: 'text-teal-600 dark:text-teal-400',
      hint: 'Product-broker access grants, not business volume',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Insurer Dashboard" subtitle="Overview of your insurance company portal" />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats
          .filter((stat) => !('withheld' in stat && stat.withheld))
          .map((stat) => {
          const to = INSURER_CARD_TARGETS[stat.label];
          const card = (
            <Card className="p-4">
              <div
                className={`flex items-center gap-3${'tooltip' in stat ? ' cursor-help' : ''}`}
                title={'hint' in stat ? stat.hint : 'tooltip' in stat ? stat.tooltip : undefined}
              >
                <stat.icon className={`h-8 w-8 ${stat.color}`} aria-hidden="true" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                  {'tooltip' in stat && <span className="sr-only">{stat.tooltip}</span>}
                  {'hint' in stat && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">{stat.hint}</p>
                  )}
                </div>
              </div>
            </Card>
          );
          // #408: a card is a link only where a matching list exists.
          return to ? (
            <Link
              key={stat.label}
              to={to}
              className="block rounded-xl transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              {card}
            </Link>
          ) : (
            <div key={stat.label}>{card}</div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Policies */}
        {data?.recent_policies !== null && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Products</h3>
            <Link
              to="/insurance/policies"
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              View all
            </Link>
          </div>
          {data?.recent_policies?.length ? (
            <div className="space-y-3">
              {data.recent_policies.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{policy.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{policy.code}</p>
                  </div>
                  <Badge variant={policy.is_active ? 'success' : 'default'}>
                    {policy.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No products yet</p>
          )}
        </Card>
        )}

        {/* Broker Relations */}
        {data?.broker_relations !== null && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Broker Relations</h3>
            {/* The approval inbox is gone (access is allow-by-default). The remaining
                per-broker lever is the product switch on the policy detail modal. */}
            <Link
              to="/insurance/policies"
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Manage
            </Link>
          </div>
          {data?.broker_relations?.length ? (
            <div className="space-y-3">
              {data.broker_relations.map((rel) => (
                <div
                  key={rel.id}
                  className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {rel.broker_name}
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[rel.status] || 'bg-gray-100 text-gray-700'}`}>
                    {rel.status.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No broker relations yet</p>
          )}
        </Card>
        )}
      </div>
    </div>
  );
}
